/**
 * SecondaryPanel - Collapsible secondary navigation/content panel
 *
 * Features:
 * - Collapsible: 280px expanded, 40px collapsed (icon-only)
 * - State persisted to localStorage
 * - Smooth animation on collapse/expand
 * - Hover to temporarily expand when collapsed (optional)
 */

import { useState, useEffect, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface SecondaryPanelProps {
  children: ReactNode;

  /** Panel title shown in header */
  title?: string;

  /** Icon shown next to title */
  icon?: ReactNode;

  /** Width when expanded (default: 280px) */
  width?: number;

  /** Width when collapsed (default: 40px) */
  collapsedWidth?: number;

  /** Whether the panel can be collapsed (default: true) */
  collapsible?: boolean;

  /** Whether to start collapsed (default: false) */
  defaultCollapsed?: boolean;

  /** localStorage key for persisting collapsed state */
  storageKey?: string;

  /** Callback when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void;

  /** Whether to expand on hover when collapsed (default: false) */
  expandOnHover?: boolean;

  /** Additional className for the panel */
  className?: string;
}

export function SecondaryPanel({
  children,
  title,
  icon,
  width = 280,
  collapsedWidth = 40,
  collapsible = true,
  defaultCollapsed = false,
  storageKey,
  onCollapsedChange,
  expandOnHover = false,
  className = '',
}: SecondaryPanelProps) {
  // Initialize collapsed state from localStorage or default
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (storageKey) {
      const stored = localStorage.getItem(`secondary-panel-${storageKey}`);
      if (stored !== null) {
        return stored === 'true';
      }
    }
    return defaultCollapsed;
  });

  const [isHovering, setIsHovering] = useState(false);

  // Persist collapsed state to localStorage
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(`secondary-panel-${storageKey}`, String(isCollapsed));
    }
    onCollapsedChange?.(isCollapsed);
  }, [isCollapsed, storageKey, onCollapsedChange]);

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Determine current width (considering hover state)
  const currentWidth = isCollapsed && !(expandOnHover && isHovering)
    ? collapsedWidth
    : width;

  // Whether content should be visible
  const showContent = !isCollapsed || (expandOnHover && isHovering);

  return (
    <aside
      className={`
        flex flex-col border-r border-line bg-white
        transition-all duration-300 ease-out
        ${className}
      `}
      style={{ width: currentWidth }}
      onMouseEnter={() => expandOnHover && setIsHovering(true)}
      onMouseLeave={() => expandOnHover && setIsHovering(false)}
    >
      {/* Header */}
      {(title || collapsible) && (
        <div
          className={`
            flex items-center gap-2 px-3 py-3 border-b border-line
            ${isCollapsed && !showContent ? 'justify-center' : 'justify-between'}
          `}
        >
          {showContent ? (
            <>
              <div className="flex items-center gap-2 min-w-0">
                {icon && (
                  <span className="text-muted flex-shrink-0">
                    {icon}
                  </span>
                )}
                {title && (
                  <h2 className="text-sm font-semibold text-text truncate">
                    {title}
                  </h2>
                )}
              </div>
              {collapsible && (
                <button
                  onClick={handleToggle}
                  className="p-1 rounded hover:bg-surface-subtle text-subtle hover:text-muted transition-colors"
                  aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
                  title={isCollapsed ? 'Expand panel' : 'Collapse panel'}
                >
                  <ChevronLeft size={16} />
                </button>
              )}
            </>
          ) : (
            <button
              onClick={handleToggle}
              className="p-1.5 rounded hover:bg-surface-subtle text-subtle hover:text-muted transition-colors"
              aria-label="Expand panel"
              title="Expand panel"
            >
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div
        className={`
          flex-1 overflow-hidden
          transition-opacity duration-200
          ${showContent ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        <div className="h-full overflow-y-auto">
          {children}
        </div>
      </div>

      {/* Collapsed state icon indicators (optional) */}
      {isCollapsed && !showContent && (
        <div className="flex-1 flex flex-col items-center py-4 gap-3">
          {/* Could add collapsed state icons here if needed */}
        </div>
      )}
    </aside>
  );
}

export default SecondaryPanel;
